/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { call, delay, put, race } from "redux-saga/effects";

import * as siteContentApi from "../../Services/SiteContentApi";

import { GENERIC_API_TIMEOUT_ERROR } from "../../Constants/ApplicationConstants";
import { HttpStatusCode } from "../../Constants/HttpStatusCode";
import {
  IGetIndustryFocusDataResult,
  IGetIndustryFocusOkResult,
} from "../../Services/Interfaces/SiteContent";
import { IApiErrorResult, IApiResult2 } from "../../Utility/IApiResult";
import { getDefaultApiTimeOutConfiguration } from "../../Utility/Utils";
import { getIndustryFocusFailure, getIndustryFocusSuccess } from "./IndustryFocusAction";

/**
 * Handle load industry focuses.
 * NOTE: Errors need to be caught and handled by the caller as since this is just a util, there is no telemetry instance.
 * @returns {void} void.
 */
function* handleLoadIndustryFocuses() {
  try {
    const { result, timeout }: IApiResult2<IGetIndustryFocusDataResult> | any = yield race({
      result: call(siteContentApi.fetchIndustryFocusList),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(getIndustryFocusFailure(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          getIndustryFocusSuccess((result.parsedBody as IGetIndustryFocusOkResult).industryFocuses)
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(getIndustryFocusFailure((result.parsedBody as IApiErrorResult).error.message));
        break;
      case HttpStatusCode.NotFound:
        yield put(getIndustryFocusFailure((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(getIndustryFocusFailure((result.parsedBody as IApiErrorResult).error.message));
        break;
    }
  } catch (error) {
    yield put(getIndustryFocusFailure(error.message));
  }
}

export { handleLoadIndustryFocuses };
